import {
  getPageHeader,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderActionPrimary,
  PageHeaderActionSecondary,
  PageHeaderActions,
  Input,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@yeshuman/sdk'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { CalendarCheck, Download, Link2, Link2Off, RefreshCw, Trash2 } from 'lucide-react'
import {
  downloadConsultReportPdf,
  useConsultReport,
  useCreateConsultShareLink,
  useDeleteConsultReport,
  useRegenerateConsultReport,
  useRevokeConsultShareLink,
} from '../hooks/use-consult-reports'
import { ConsultReportView } from './consult-report-view'
import { toast } from 'sonner'

export function ConsultReportDetailPage() {
  const { reportId } = useParams<{ reportId: string }>()
  const navigate = useNavigate()
  const { report, isLoading, error, refetch } = useConsultReport(reportId)
  const regenerate = useRegenerateConsultReport()
  const createShare = useCreateConsultShareLink()
  const revokeShare = useRevokeConsultShareLink()
  const deleteReport = useDeleteConsultReport()
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [pdfBusy, setPdfBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const { title } = getPageHeader(
    '/consultations',
    'Consultation prep',
    'Review, regenerate, share, or download your pack.',
  )

  const onRegenerate = async () => {
    if (!reportId) return
    try {
      await regenerate.mutateAsync(reportId)
      toast.success('Report regenerated')
      refetch()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to regenerate')
    }
  }

  const onShare = async () => {
    if (!reportId) return
    try {
      const result = await createShare.mutateAsync(reportId)
      setShareUrl(result.share_url)
      await navigator.clipboard.writeText(result.share_url)
      toast.success('Share link copied')
      refetch()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create share link')
    }
  }

  const onRevoke = async () => {
    if (!reportId) return
    try {
      await revokeShare.mutateAsync(reportId)
      setShareUrl(null)
      toast.success('Share link revoked')
      refetch()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke share link')
    }
  }

  const onPdf = async () => {
    if (!reportId) return
    setPdfBusy(true)
    try {
      await downloadConsultReportPdf(reportId)
      toast.success('PDF downloaded')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to download PDF')
    } finally {
      setPdfBusy(false)
    }
  }

  const onDelete = async () => {
    if (!reportId) return
    try {
      await deleteReport.mutateAsync(reportId)
      toast.success('Consultation report deleted')
      setConfirmDelete(false)
      navigate('/consultations')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete')
    }
  }

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title={report?.payload?.headline || title}
        description="Patient-prepared consultation pack"
        icon={<CalendarCheck className="h-5 w-5" />}
        actions={
          report ? (
            <PageHeaderActions
              primary={
                report.status === 'ready' ? (
                  <PageHeaderActionPrimary
                    onClick={onRegenerate}
                    disabled={regenerate.isPending}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Regenerate
                  </PageHeaderActionPrimary>
                ) : undefined
              }
              secondary={
                <PageHeaderActionSecondary
                  onClick={() => setConfirmDelete(true)}
                  disabled={deleteReport.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </PageHeaderActionSecondary>
              }
            />
          ) : undefined
        }
      />

      <div className="mb-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/consultations">← All consultation prep reports</Link>
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardHeader>
            <CardTitle>Loading…</CardTitle>
          </CardHeader>
        </Card>
      ) : null}

      {error ? (
        <Card>
          <CardHeader>
            <CardTitle>Could not load report</CardTitle>
            <CardDescription>
              {error instanceof Error ? error.message : 'Something went wrong'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {report ? (
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <ConsultReportView
                payload={report.payload}
                visitLabel={report.visit_label}
                visitAt={report.visit_at}
                showDisclaimerBanner
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Share & download</CardTitle>
              <CardDescription>
                Create a secret link for this pack, or download a simple PDF.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={onShare}
                  disabled={report.status !== 'ready' || createShare.isPending}
                >
                  <Link2 className="h-4 w-4" />
                  {report.share_active ? 'Copy new share link' : 'Create share link'}
                </Button>
                <Button
                  variant="outline"
                  onClick={onRevoke}
                  disabled={!report.share_active || revokeShare.isPending}
                >
                  <Link2Off className="h-4 w-4" />
                  Revoke link
                </Button>
                <Button
                  variant="outline"
                  onClick={onPdf}
                  disabled={report.status !== 'ready' || pdfBusy}
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </Button>
              </div>
              {shareUrl ? (
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground" htmlFor="consult-share-url">
                    Share URL
                  </label>
                  <Input id="consult-share-url" name="consult-share-url" readOnly value={shareUrl} />
                </div>
              ) : null}
              {report.share_active && report.share_expires_at ? (
                <p className="text-xs text-muted-foreground">
                  Current link expires {new Date(report.share_expires_at).toLocaleString()}
                </p>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Visit intent</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap text-sm">{report.intent_text}</p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this consultation report?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the prep pack and any active share link. You cannot undo
              this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void onDelete()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
